import React, { useEffect, useState } from "react";
import { Link, NavLink, Routes, Route, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Receipt, Heart, Settings, User as UserIcon, Download, Key, Copy, ChevronRight } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/dashboard" replace />;

  const items = [
    { to: "/dashboard", icon: UserIcon, label: t("common.dashboard"), end: true },
    { to: "/dashboard/orders", icon: Receipt, label: t("common.orders") },
    { to: "/dashboard/wishlist", icon: Heart, label: t("common.wishlist") },
    { to: "/dashboard/settings", icon: Settings, label: t("common.settings") },
  ];

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <div className="gf-container py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <aside className="rounded-3xl bg-white border border-slate-200 p-6 h-fit">
            <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#A855F7] to-[#7E22CE] grid place-items-center text-white font-display font-bold">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-slate-900 truncate">{user.name}</div>
                <div className="text-xs text-slate-500 truncate">{user.email}</div>
              </div>
            </div>
            <nav className="mt-4 space-y-1">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive ? "bg-[#A855F7] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`dash-nav-${it.label}`}
                >
                  <it.icon className="w-4 h-4" /> {it.label}
                </NavLink>
              ))}
            </nav>
          </aside>

          <main>
            <Routes>
              <Route index element={<DashboardHome />} />
              <Route path="orders" element={<Orders />} />
              <Route path="wishlist" element={<WishlistView />} />
              <Route path="settings" element={<SettingsView />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}

function DashboardHome() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders/me").then((r) => setOrders(r.data)); }, []);
  const paid = orders.filter((o) => o.payment_status === "paid");
  const totalSpent = paid.reduce((s, o) => s + o.total, 0);

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back, {user.name}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Orders", value: orders.length, color: "from-[#A855F7] to-[#7E22CE]" },
          { label: "Paid", value: paid.length, color: "from-emerald-400 to-emerald-600" },
          { label: "Total spent", value: formatPrice(totalSpent), color: "from-amber-400 to-amber-600" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.color} mb-3`} />
            <div className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">{s.value}</div>
          </motion.div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Recent orders</h3>
        {orders.slice(0, 5).map((o) => (
          <Link key={o.id} to="/dashboard/orders" className="flex items-center justify-between py-3 border-b last:border-0 border-slate-100 hover:bg-slate-50 -mx-3 px-3 rounded-lg transition-colors">
            <div>
              <div className="text-sm font-semibold text-slate-900">#{o.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-sm font-bold text-slate-900">{formatPrice(o.total)}</div>
          </Link>
        ))}
        {orders.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No orders yet.</div>}
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders/me").then((r) => setOrders(r.data)); }, []);

  const copyKey = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Key copied");
  };

  return (
    <div data-testid="orders-list">
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">My Orders</h1>
      {orders.length === 0 && <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">No orders yet.</div>}
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-slate-500 uppercase">Order</div>
                <div className="font-display font-bold text-slate-900">#{o.id.slice(0, 8).toUpperCase()}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Date</div>
                <div className="text-sm">{new Date(o.created_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Total</div>
                <div className="font-display font-bold text-slate-900">{formatPrice(o.total)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Status</div>
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  o.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                }`}>{o.status}</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              {o.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <img src={it.cover_image} alt={it.title} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{it.title}</div>
                    <div className="text-xs text-slate-500">Qty: {it.quantity} • {formatPrice(it.line_total)}</div>
                  </div>
                </div>
              ))}
              {o.keys?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Key className="w-3 h-3" /> Activation Keys</div>
                  <div className="space-y-2">
                    {o.keys.map((k, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 font-mono text-sm">
                        <span className="flex-1 truncate">{k.key}</span>
                        <button onClick={() => copyKey(k.key)} className="text-[#A855F7] hover:bg-purple-50 p-1 rounded" data-testid={`copy-key-${i}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WishlistView() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data.items || [])); }, []);
  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">Wishlist</h1>
      {items.length === 0 ? <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">No wishlisted items.</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-neon-soft transition-all">
              <img src={p.cover_image} alt={p.title} className="w-16 h-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-900 truncate">{p.title}</div>
                <div className="text-sm text-[#A855F7] font-bold">{formatPrice(p.price)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsView() {
  const { user } = useAuth();
  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">Settings</h1>
      <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4 max-w-lg">
        <div>
          <div className="text-xs text-slate-500 uppercase mb-1">Name</div>
          <div className="font-medium">{user.name}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase mb-1">Email</div>
          <div className="font-medium">{user.email}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase mb-1">Role</div>
          <div className="font-medium capitalize">{user.role}</div>
        </div>
      </div>
    </div>
  );
}
